import { Empty, Steps } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import {
  formatDateUtcToCurrent,
  formatDateUtcToCurrentTz,
} from "../../utils/utils";
import { useDispatch } from "react-redux";
import { getActivityLogs } from "../../../core/store/reducers/activity-log.slice";
import { getUsersDetails } from "../../../core/store/reducers/auth.slice";

interface UpdatedUser {
  fullName: string;
}

interface ActivityEntry {
  subject: string;
  newValue?: string;
  oldvalue?: string;
  newvalue?: string;
  contents?: string;
  updatedUserDetails?: UpdatedUser[];
  [key: string]: any;
}

interface FieldChange {
  fieldCode: string;
  fieldName: string;
  oldValue: string | null;
  newValue: string | null;
}

interface FormattedActivity {
  subject: string;
  fieldName: string;
  oldValue: string;
  newValue: string;
  content: any;
  updatedBy: string;
  [key: string]: any;
}

type ActivityObjectPart = {
  label: string;
  value: string;
};

type ActivityStep = {
  title: string;
  description: string | React.ReactNode;
  status: string;
};

type ActivityLogProps = {
  
  id?: string;
  record?: any;
  module?: any;
};

const hiddenActivityKeys = new Set([
  "id",
  "createdBy",
  "created_by",
  "createdDate",
  "created_date",
  "updatedBy",
  "updated_by",
  "updatedDate",
  "updated_date",
]);

const hiddenActivityFieldCodes = new Set([
  "totalExperiencePriorToJoining",
]);

const activityValueLabels: Record<string, string> = {
  bonusAmount: "Amount",
  effectiveFrom: "Effective From",
  issuedDate: "Issued Date",
  nextSalaryRevisionDate: "Next Revision",
  previousSalary: "Current Salary",
  previousSalaryRevisionAmount: "Previous Salary",
  previousSalaryRevisionDate: "Previous Revision",
};

const activityFieldLabels: Record<string, string> = {
  bonusAmount: "Bonus Amount",
  bonusHistory: "Bonus History",
  ctc: "CTC",
  department: "Department",
  effectiveFrom: "Effective From",
  issuedDate: "Issued Date",
  lineManager: "Line Manager",
  nextSalaryRevisionDate: "Next Salary Revision Date",
  positionHistory: "Position History",
  previousSalary: "Current Salary",
  previousSalaryRevisionAmount: "Previous Salary Revision Amount",
  previousSalaryRevisionDate: "Previous Salary Revision Date",
  projectTimeline: "Project Timeline",
  resignationDate: "Resignation Date",
  salaryCurrency: "Salary Currency",
  salaryRevisionHistory: "Salary Revision History",
  skillDetails: "Skill Details",
};

const parseActivityValue = (value: any) => {
  if (typeof value !== "string") {
    return value;
  }

  const trimmedValue = value.trim();
  if (!trimmedValue || !["[", "{"].includes(trimmedValue[0])) {
    return value;
  }

  try {
    return JSON.parse(trimmedValue);
  } catch {
    return value;
  }
};

const formatActivityDate = (value: any) => {
  if (!value) {
    return "";
  }

  const date = moment(value, [moment.ISO_8601, "YYYY-MM-DD"], true);
  return date.isValid() ? date.format("DD-MMM-YYYY") : String(value);
};

const formatActivityAmount = (value: any) => {
  if (value === null || typeof value === "undefined" || value === "") {
    return "";
  }

  const amount = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(amount)
    ? amount.toLocaleString("en-IN", { maximumFractionDigits: 2 })
    : String(value);
};

const humanizeActivityKey = (key: string) =>
  activityValueLabels[key] ||
  key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getActivityFieldLabel = (fieldCode: string, fieldName: string) =>
  activityFieldLabels[fieldCode] || fieldName || humanizeActivityKey(fieldCode);

const activityObjectDisplayKeys = [
  "name",
  "label",
  "value",
  "skillName",
  "technologyName",
  "documentName",
  "fileName",
  "originalFileName",
  "displayName",
  "title",
];

const groupedActivityFields: Record<string, string[]> = {
  bonusHistory: ["bonusAmount", "issuedDate"],
  salaryRevisionHistory: [
    "ctc",
    "effectiveFrom",
    "previousSalary",
    "previousSalaryRevisionAmount",
    "previousSalaryRevisionDate",
  ],
  skillDetails: [
    "primarySkills",
    "primarySkill",
    "secondarySkills",
    "secondarySkill",
    "skillProficiencyLevel",
    "proficiencyLevel",
  ],
};

const shouldSkipGroupedActivityField = (
  fieldCode: string,
  changedFieldCodes: Set<string>
) =>
  Object.entries(groupedActivityFields).some(
    ([groupFieldCode, childFieldCodes]) =>
      changedFieldCodes.has(groupFieldCode) && childFieldCodes.includes(fieldCode)
  );

const formatActivityFieldValue = (key: string, value: any): string => {
  const parsedValue = parseActivityValue(value);

  if (parsedValue === null || typeof parsedValue === "undefined" || parsedValue === "") {
    return "";
  }

  if (Array.isArray(parsedValue)) {
    return parsedValue
      .map((item) => formatActivityFieldValue(key, item))
      .filter(Boolean)
      .join(", ");
  }

  if (typeof parsedValue === "object") {
    const displayKey = activityObjectDisplayKeys.find(
      (displayKey) =>
        parsedValue[displayKey] !== null &&
        typeof parsedValue[displayKey] !== "undefined" &&
        parsedValue[displayKey] !== ""
    );

    if (displayKey) {
      return formatActivityFieldValue(displayKey, parsedValue[displayKey]);
    }

    return getActivityObjectParts(parsedValue)
      .map((part: ActivityObjectPart) => `${part.label}: ${part.value}`)
      .join(", ");
  }

  if (key.toLowerCase().includes("date") || key === "effectiveFrom") {
    return formatActivityDate(parsedValue);
  }

  const normalizedKey = key.toLowerCase();

  if (
    normalizedKey.includes("salary") ||
    normalizedKey.includes("amount") ||
    normalizedKey.includes("ctc") ||
    normalizedKey.includes("cost")
  ) {
    return formatActivityAmount(parsedValue);
  }

  return String(parsedValue);
};

const getActivityObjectParts = (value: Record<string, any>): ActivityObjectPart[] =>
  Object.entries(value)
    .filter(
      ([key, fieldValue]) =>
        !hiddenActivityKeys.has(key) &&
        fieldValue !== null &&
        typeof fieldValue !== "undefined" &&
        fieldValue !== ""
    )
    .map(([key, fieldValue]) => ({
      label: humanizeActivityKey(key),
      value: formatActivityFieldValue(key, fieldValue),
    }))
    .filter((item): item is ActivityObjectPart => Boolean(item.value));

const getComparableActivityText = (value: any, fieldName?: string): string => {
  const parsedValue = parseActivityValue(value);

  if (parsedValue === null || typeof parsedValue === "undefined" || parsedValue === "") {
    return "";
  }

  if (
    typeof parsedValue === "string" ||
    typeof parsedValue === "number" ||
    typeof parsedValue === "boolean"
  ) {
    return fieldName
      ? formatActivityFieldValue(fieldName, parsedValue)
      : String(parsedValue);
  }

  if (Array.isArray(parsedValue)) {
    return parsedValue
      .map((item) =>
        item && typeof item === "object"
          ? getActivityObjectParts(item)
              .sort((a, b) => a.label.localeCompare(b.label))
              .map((part) => `${part.label}:${part.value}`)
              .join("|")
          : formatActivityFieldValue(fieldName || "", item)
      )
      .filter(Boolean)
      .sort()
      .join("||");
  }

  if (typeof parsedValue === "object") {
    return getActivityObjectParts(parsedValue)
      .sort((a, b) => a.label.localeCompare(b.label))
      .map((part) => `${part.label}:${part.value}`)
      .join("|");
  }

  return String(parsedValue);
};

const hasDisplayableActivityValue = (value: any): boolean => {
  const parsedValue = parseActivityValue(value);

  if (parsedValue === null || typeof parsedValue === "undefined" || parsedValue === "") {
    return false;
  }

  if (
    typeof parsedValue === "string" ||
    typeof parsedValue === "number" ||
    typeof parsedValue === "boolean"
  ) {
    return String(parsedValue).trim() !== "";
  }

  if (Array.isArray(parsedValue)) {
    return parsedValue.some((item) =>
      item && typeof item === "object"
        ? getActivityObjectParts(item).length > 0
        : String(formatActivityFieldValue("", item)).trim() !== ""
    );
  }

  if (typeof parsedValue === "object") {
    return getActivityObjectParts(parsedValue).length > 0;
  }

  return false;
};

const ActivityLogComponent: React.FC<ActivityLogProps> = ({
  // Moved formatValue outside the component to prevent re-creation on every render.
  // This ensures it's a stable utility function.
  // The actual fix involves applying this formatter when populating the `arrResponse`
  // to ensure `oldValue` and `newValue` are always strings, preventing React from
  // attempting to render raw objects.

  id,
  record,
  module
}: ActivityLogProps) => {
  const dispatch = useDispatch();
  const [activityLogs, setActivityLogs] = useState<any[]>([]);
  const [activityLogData, setActivityLogData] = useState<any[]>([]);
  const [userDetails, setUserDetails] = useState<any>({})

  const formatValue = (value: any, fieldName?: string): React.ReactNode => {
    const parsedValue = parseActivityValue(value);

    if (parsedValue === null || typeof parsedValue === "undefined") {
      return "";
    }

    if (
      typeof parsedValue === "string" ||
      typeof parsedValue === "number" ||
      typeof parsedValue === "boolean"
    ) {
      if (fieldName) {
        return formatActivityFieldValue(fieldName, parsedValue);
      }
      return String(parsedValue);
    }

    if (Array.isArray(parsedValue)) {
      const rows = parsedValue
        .map((item) =>
          item && typeof item === "object"
            ? getActivityObjectParts(item)
            : [{ label: "", value: formatActivityFieldValue(fieldName || "", item) }]
        )
        .filter((parts) => parts.length);

      if (!rows.length) {
        return "";
      }

      return (
        <div className="activity-log-value-list">
          {rows.map((parts, index) => (
            <div className="activity-log-value-row" key={index}>
              {rows.length > 1 && (
                <span className="activity-log-value-index">{index + 1}.</span>
              )}
              <span>
                {parts.map((part, partIndex) => (
                  <span className="activity-log-value-part" key={`${part.label}-${partIndex}`}>
                    {part.label && <span>{part.label}: </span>}
                    <strong>{part.value}</strong>
                    {partIndex < parts.length - 1 ? ", " : ""}
                  </span>
                ))}
              </span>
            </div>
          ))}
        </div>
      );
    }

    if (typeof parsedValue === "object") {
      const parts = getActivityObjectParts(parsedValue);
      if (!parts.length) {
        return "";
      }

      return (
        <span>
          {parts.map((part, index) => (
            <span className="activity-log-value-part" key={part.label}>
              <span>{part.label}: </span>
              <strong>{part.value}</strong>
              {index < parts.length - 1 ? ", " : ""}
            </span>
          ))}
        </span>
      );
    }

    return String(parsedValue);
  };

  useEffect(() => {
    if (id ) {
      fetchActivityLog();
      fetchUserDetails();
    }
  }, []);

  
  const fetchUserDetails = async () => {
    const response = await dispatch(getUsersDetails({ids: [record.createdBy]})).unwrap();
    console.log(response?.data, "getUsersDetails")
    if (response?.data[0]) {
      setUserDetails(response?.data[0])
    }
  };

  const fetchActivityLog = async () => {
    const response = await dispatch(getActivityLogs(id)).unwrap();
    if (response?.data?.data) {
      setActivityLogData(response?.data?.data);
      console.log(response?.data?.data, "fetchActivityLog");
    }
  };

  const isJson = (str: string | undefined): boolean => {
    if (!str) return false;
    try {
      JSON.parse(str);
      return true;
    } catch (e) {
      return false;
    }
  };

  const isBlankActivityValue = (value: any) =>
    value === null || value === undefined || value === "";

  const shouldSkipStaffNullRemoval = (subject: string, change: FieldChange) =>
    module === "Staff" &&
    subject.includes("Update") &&
    !isBlankActivityValue(change.oldValue) &&
    isBlankActivityValue(change.newValue);

  useEffect(() => {
    setActivityLogs([]);
    handleFormatActivity();
  }, [activityLogData,userDetails]);

  const handleFormatActivity = () => {
    const arrResponse: FormattedActivity[] = [];

    

    activityLogData.forEach((entry: any) => {
      const {
        subject,
        newValue,
        contents,
        updatedUserDetails,
        oldvalue,
        newvalue,
      } = entry;
      const updatedBy = updatedUserDetails?.[0]
        ? `${updatedUserDetails[0].fullName} `
        : "System";

      if (subject.includes("Update") && isJson(newValue) && JSON.parse(newValue)?.length) {
        const parsedChanges: FieldChange[] = JSON.parse(newValue as string);
        const changedFieldCodes = new Set(
          parsedChanges.map((change) => change.fieldCode).filter(Boolean)
        );

        parsedChanges.forEach((change) => {
          const { fieldCode, fieldName } = change;
          if (hiddenActivityFieldCodes.has(fieldCode)) {
            return;
          }

          if (shouldSkipGroupedActivityField(fieldCode, changedFieldCodes)) {
            return;
          }

          // Only include changed fields
          if (change.oldValue !== change.newValue) {
            if (shouldSkipStaffNullRemoval(subject, change)) return;

            let oldVal = change.oldValue ?? "";
            let newVal = change.newValue ?? "";

            // Check if values are valid date strings
            const isOldDate = moment(oldVal, moment.ISO_8601, true).isValid();
            const isNewDate = moment(newVal, moment.ISO_8601, true).isValid();

            if (isOldDate) {
              oldVal = moment.utc(oldVal).local().format("DD-MMM-YYYY");
            }

            if (isNewDate) {
              newVal = moment.utc(newVal).local().format("DD-MMM-YYYY");
            }

            const displayFieldName = getActivityFieldLabel(fieldCode, fieldName);
            if (
              getComparableActivityText(oldVal, displayFieldName) ===
              getComparableActivityText(newVal, displayFieldName)
            ) {
              return;
            }

            arrResponse.push({
              ...entry,
              subject: subject,
              fieldName: displayFieldName,
              oldValue: oldVal,
              newValue: newVal,
              content: isJson(contents) ? JSON.parse(contents as string) : {},
              updatedBy: updatedBy,
            });
          }
        });
      } else {
        // Handle simple audit logs.
        // The API returns camelCase oldValue/newValue; the lowercase aliases are
        // kept as a fallback for older payloads. Without the camelCase fallback
        // every simple log rendered with a blank old -> new.
        arrResponse.push({
          ...entry,
          fieldName: subject,
          oldValue: oldvalue ?? entry?.oldValue ?? "",
          newValue: newvalue ?? entry?.newValue ?? "",
          content: isJson(contents) ? JSON.parse(contents as string) : {},
          updatedBy: updatedBy,
          // Use this entry's own timestamp - falling back to the parent record's
          // created date stamped every log with the record creation time.
          created_date: entry?.created_date ?? entry?.createdDate ?? record?.createdDate,
        });
      }
    });

    const hasCreateActivityLog = arrResponse.some(
      (entry) =>
        entry.subject === `Create ${module}` ||
        entry.subject === `${module} Created`
    );

    if (!hasCreateActivityLog) {
      arrResponse.push({
        subject: `${module} Created`,
        fieldName: "",
        oldValue: "",
        newValue: "",
        content: '',
        updatedBy: userDetails.fullName ? userDetails?.fullName : '',
        created_date: record.createdDate
      });
    }
    setActivityLogs(arrResponse);
    console.log(arrResponse, "arrResponse");
  };

  return (
    <>
    <Steps
      direction="vertical"
      size="small"
      current={activityLogs.length}
      items={activityLogs.map((step, index) => {
        const hasOldValue = hasDisplayableActivityValue(step.oldValue);
        const hasNewValue = hasDisplayableActivityValue(step.newValue);

        return {
          title: (
            <>
              <div>{hasOldValue || hasNewValue ? step.fieldName : step.subject}</div>
              {(hasOldValue || hasNewValue) && (
                <div className="weight-600">
                  {hasOldValue && hasNewValue && (
                    <>
                      {formatValue(step.oldValue, step.fieldName)} {"→"}{" "}
                      {formatValue(step.newValue, step.fieldName)}
                    </>
                  )}

                  {hasOldValue && !hasNewValue && (
                    <>
                      {formatValue(step.oldValue, step.fieldName)} {"→"}{" "}
                      <span style={{ color: "red" }}>Removed</span>
                    </>
                  )}

                  {!hasOldValue && hasNewValue && (
                    <>{formatValue(step.newValue, step.fieldName)}</>
                  )}
                </div>
              )}
            </>
          ),

          description: `by ${step.updatedBy || "---"} | ${
            step.created_date
              ? formatDateUtcToCurrent(step.created_date, "DD-MMM-YYYY hh:mm A")
              : ""
          }`,
          // status: step.status as any,
          icon:
            step.status === "error" ? (
              <div
                style={{
                  color: "#ff4d4f",
                  border: "1px solid #ff4d4f",
                  borderRadius: "50%",
                  width: 24,
                  height: 24,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                }}
              >
                {index + 1}
              </div>
            ) : undefined,
        };
      })}
    />
    {activityLogs?.length === 0 && 
      <Empty/>
    }
    </>
    
  );
};

export default ActivityLogComponent;
